import { useEffect, useState } from "react";
import type { ServiceConnections } from "@voice/api-contract";

export type ConnectionFocus = {
  numberId?: string;
  deviceId?: string;
  lineId?: string;
};
export type ConnectionState = {
  data: ServiceConnections | null;
  error: string;
  reload: () => void;
};
export async function readConnectionSnapshot(
  api: <T>(path: string, options?: { signal?: AbortSignal }) => Promise<T>,
  path: string,
  signal: AbortSignal,
  timeoutMs = 10_000,
): Promise<ServiceConnections> {
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  let cancel = () => {};
  const interrupted = new Promise<never>((_resolve, reject) => {
    cancel = () => {
      controller.abort();
      reject(new Error("Connection check canceled."));
    };
    if (signal.aborted) cancel();
    else {
      signal.addEventListener("abort", cancel, { once: true });
      timer = setTimeout(() => {
        controller.abort();
        reject(
          new Error(
            "The connection status check timed out. Retrying automatically.",
          ),
        );
      }, timeoutMs);
    }
  });
  try {
    return await Promise.race([
      Promise.resolve().then(() => {
        if (controller.signal.aborted)
          throw new Error("Connection check canceled.");
        return api<ServiceConnections>(path, { signal: controller.signal });
      }),
      interrupted,
    ]);
  } finally {
    clearTimeout(timer);
    signal.removeEventListener("abort", cancel);
  }
}
export function clearConnectionObservations(
  data: ServiceConnections,
): ServiceConnections {
  return {
    ...data,
    lines: data.lines.map((line) => ({
      ...line,
      observation: {
        registration: "unknown",
        configuration: "unknown",
        freshness: "unavailable",
        checkedAt: null,
      },
    })),
  };
}
export function useAccountConnections(
  api: <T>(path: string, options?: { signal?: AbortSignal }) => Promise<T>,
  accountId: string,
  customer: boolean,
  refreshVersion: number,
): ConnectionState {
  const [data, setData] = useState<ServiceConnections | null>(null);
  const [error, setError] = useState("");
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    setData(null);
    setError("");
    let busy = false;
    const refresh = async (background = false) => {
      if (busy || controller.signal.aborted) return;
      busy = true;
      await readConnectionSnapshot(
        api,
        `/${customer ? "customer" : "admin"}/accounts/${accountId}/connections`,
        controller.signal,
      )
        .then((result) => {
          if (!controller.signal.aborted) {
            setData(result);
            setError("");
          }
        })
        .catch((cause) => {
          if (!controller.signal.aborted) {
            setData((previous) =>
              background && previous
                ? clearConnectionObservations(previous)
                : null,
            );
            setError(cause.message);
          }
        });
      busy = false;
    };
    void refresh();
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") void refresh(true);
    }, 15000);
    const onVisible = () => {
      if (document.visibilityState === "visible") void refresh(true);
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      controller.abort();
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [api, accountId, customer, refreshVersion, revision]);
  return { data, error, reload: () => setRevision((value) => value + 1) };
}

export function LineRegistration({
  line,
}: {
  line: ServiceConnections["lines"][number];
}) {
  const observation = line.observation;
  const age = observation?.checkedAt
    ? Date.now() - Date.parse(observation.checkedAt)
    : NaN;
  const current =
    observation?.freshness === "current" && age >= -5000 && age <= 60000;
  const registration = current ? observation.registration : "unknown";
  const labels = {
    registered: "SIP registered",
    offline: "SIP not registered",
    not_configured: "SIP endpoint not configured",
    unknown: "Live registration unavailable",
  };
  return (
    <div className="cr-relationship">
      <strong>{labels[registration]}</strong>
      <small>
        {current && observation.configuration === "matched"
          ? "Installed connection matches this saved assignment."
          : current && observation.configuration === "pending"
            ? "Saved connection differs from the installed configuration."
            : "Installed connection has not been confirmed for this assignment."}
      </small>
      {observation?.checkedAt && (
        <small>
          Checked {new Date(observation.checkedAt).toLocaleTimeString()}
          {!current ? " · Status is stale" : " · Refreshes every 15 seconds"}
        </small>
      )}
      <small>
        Registration does not verify call audio or feature settings.
      </small>
    </div>
  );
}

export const equipmentIdentity = (
  device: ServiceConnections["devices"][number],
) =>
  `${device.label ? `${device.label} · ` : ""}${device.model} · ${device.hardwareId?.startsWith("softphone:") ? "Logical softphone" : (device.hardwareId?.match(/.{2}/g)?.join(":") ?? "MAC unavailable")}`;
export const lineIdentity = (line: ServiceConnections["lines"][number]) =>
  line.digits ? `Extension ${line.digits} · ${line.label}` : line.label;

export function numberDestination(data: ServiceConnections, numberId: string) {
  const number = data.numbers.find((item) => item.id === numberId);
  if (!number) return "Number not found in connection records";
  if (["released", "aging", "failed"].includes(number.status))
    return "Not available for calling";
  const line = data.lines.find((item) => item.id === number.lineId);
  if (!line)
    return number.lineId
      ? "Saved destination needs review"
      : "Not assigned to a phone";
  if (line.status === "disconnected")
    return `${lineIdentity(line)} · Disconnected line`;
  const device = data.devices.find((item) => item.id === line.deviceId);
  if (!device) return `${lineIdentity(line)} · Equipment not assigned`;
  return `${lineIdentity(line)} → ${equipmentIdentity(device)} → Port ${line.deviceSlot ?? "unconfirmed"} · ${line.locationName}${["retired", "quarantined"].includes(device.status) ? " · Equipment unavailable" : ""}`;
}

function Unavailable({ state }: { state: ConnectionState }) {
  return (
    <span>
      {state.error
        ? "Connections unavailable. Use Refresh connections above."
        : "Loading connections…"}
    </span>
  );
}

export function NumberConnection({
  state,
  numberId,
  onEdit,
}: {
  state: ConnectionState;
  numberId: string;
  onEdit?: ((focus: ConnectionFocus) => void) | undefined;
}) {
  if (!state.data) return <Unavailable state={state} />;
  const number = state.data.numbers.find((item) => item.id === numberId);
  const recorded = state.data.lines.find(
    (item) => item.id === number?.recordedLineId,
  );
  return (
    <div className="cr-relationship">
      <span>{numberDestination(state.data, numberId)}</span>
      {number?.lineId && (
        <small>Saved assignment; live calling not verified</small>
      )}
      {number?.recordedLineId && number.recordedLineId !== number.lineId && (
        <small>
          Earlier system record:{" "}
          {recorded ? lineIdentity(recorded) : "another line"}. Reconciliation
          needed.
        </small>
      )}
      {onEdit && number?.eligible && (
        <button className="cr-text-button" onClick={() => onEdit({ numberId })}>
          {number.lineId ? "Change assignment" : "Assign to equipment"}
        </button>
      )}
    </div>
  );
}

export function DeviceConnections({
  state,
  deviceId,
  onEdit,
}: {
  state: ConnectionState;
  deviceId: string;
  onEdit?: ((focus: ConnectionFocus) => void) | undefined;
}) {
  if (!state.data) return <Unavailable state={state} />;
  const data = state.data;
  const device = data.devices.find((item) => item.id === deviceId);
  const lines = data.lines.filter((item) => item.deviceId === deviceId);
  return (
    <div className="cr-relationship">
      {lines.length ? (
        lines.map((line) => (
          <div key={line.id}>
            <strong>
              Port {line.deviceSlot ?? "unconfirmed"} · {lineIdentity(line)}
            </strong>
            <span>
              {data.numbers
                .filter((number) => number.lineId === line.id)
                .map((number) => number.number)
                .join(", ") || "No incoming number"}
            </span>
            {line.status === "disconnected" ? (
              <small>Disconnected line</small>
            ) : (
              <LineRegistration line={line} />
            )}
            {onEdit && (
              <button
                className="cr-text-button"
                onClick={() => onEdit({ deviceId, lineId: line.id })}
              >
                Change this connection
              </button>
            )}
          </div>
        ))
      ) : (
        <span>No line or incoming number assigned</span>
      )}
      {device?.recordedAssignments
        ?.filter((item) => !lines.some((line) => line.id === item.lineId))
        .map((item) => (
          <small key={item.lineId}>
            Earlier system assignment: {item.lineLabel}. Port is not recorded;
            review before reuse.
          </small>
        ))}
      {onEdit &&
        device &&
        !["retired", "quarantined"].includes(device.status) && (
          <button
            className="cr-text-button"
            onClick={() => onEdit({ deviceId })}
          >
            Assign a number or extension
          </button>
        )}
    </div>
  );
}

export function ExtensionConnection({
  state,
  extensionId,
  onEdit,
}: {
  state: ConnectionState;
  extensionId: string;
  onEdit?: ((focus: ConnectionFocus) => void) | undefined;
}) {
  if (!state.data) return <Unavailable state={state} />;
  const data = state.data;
  const line = data.lines.find((item) => item.extensionId === extensionId);
  if (!line)
    return (
      <p>Connection record unavailable. Refresh connections before editing.</p>
    );
  const device = data.devices.find((item) => item.id === line.deviceId);
  return (
    <div className="cr-relationship">
      <strong>
        {device
          ? `${equipmentIdentity(device)} · Port ${line.deviceSlot ?? "unconfirmed"}`
          : "Equipment not assigned"}
      </strong>
      <LineRegistration line={line} />
      <span>
        Incoming numbers:{" "}
        {data.numbers
          .filter((number) => number.lineId === line.id)
          .map((number) => number.number)
          .join(", ") || "None assigned"}
      </span>
      {onEdit && (
        <button
          className="cr-text-button"
          onClick={() => onEdit({ lineId: line.id })}
        >
          Assign equipment and numbers
        </button>
      )}
    </div>
  );
}
